import * as THREE from 'three';
import { glowRingTexture } from '@/world/scenery/propKit';
import { itemFxCalm } from '@/entities/fx/itemFx';

/**
 * MagnetField.ts — the MAGNET item's on-track look (M145, owner: "add a
 * magnetic field animation pulsing from the car and to the ball that is well
 * visible by the user when driving"). Until this round the magnet had NO
 * visual at all: the ball just stuck to the nose and the only cue was the HUD
 * button's drain ring.
 *
 * Three layers, all parented to the car's group (so they live in CAR-LOCAL
 * space: +Z = nose, +X = the car's left on screen, y = up from the road):
 *
 *  1. FIELD LINES — eight glowing arcs that leave the car's flanks and roof,
 *     bow outward like a real dipole field and converge on the ball. Bright
 *     pulses run along each one FROM the car TO the ball. One mesh, one draw
 *     call: the curve is evaluated in the vertex shader from the live ball
 *     position, and each ribbon is turned to face the camera so it never goes
 *     edge-on from the chase view.
 *  2. PULSE ARCHES — half-rings that leave the tail of the car and travel
 *     forward onto the ball, closing in as they go. From the chase camera you
 *     look straight down that axis, so they read as a tunnel of pulses.
 *  3. THE CAPTURED BALL — a soft violet glow behind the ball and a tilted
 *     ring spinning round it, so the ball itself reads as "locked".
 *  4. GROUND PULSES — rings that spread across the road from under the car,
 *     so the field also reads in the part of the screen the driver watches.
 *
 * Normal (not additive) blending on purpose: additive violet vanishes over
 * Dakar's bright sand. HDR colour values (> 1) still feed the bloom pass at
 * night. Presentation only — never read by the sim.
 */

const ARC_SEGS = 22;
const ARCH_COUNT = 4;
const GROUND_RINGS = 3;

const VIOLET = new THREE.Color(0xa56bff);
const CORE = new THREE.Color(0xf2e6ff);

/** start = where the line leaves the car, bow = how far its middle swings out. */
const ARCS: { start: [number, number, number]; bow: [number, number, number]; width: number }[] = [];
for (const s of [-1, 1]) {
  ARCS.push(
    // Low and wide, off the rear corners — the big outer loops.
    { start: [s * 1.05, 0.55, -1.75], bow: [s * 4.0, 1.0, -0.4], width: 0.24 },
    // Off the doors, mid height.
    { start: [s * 1.0, 0.85, -0.5], bow: [s * 2.6, 1.9, 0.2], width: 0.2 },
    // Over the roof — the arcs the chase camera sees best.
    { start: [s * 0.45, 1.2, -1.3], bow: [s * 1.0, 3.8, 0.0], width: 0.22 },
    // Short inner lines off the front wings.
    { start: [s * 0.85, 0.6, 1.5], bow: [s * 1.6, 1.2, 0.3], width: 0.16 },
  );
}

const ARC_VERT = /* glsl */ `
  attribute vec3 aStart;
  attribute vec3 aBow;
  attribute vec3 aParam; // x = along 0..1, y = across -1..1, z = width
  attribute float aPhase;
  uniform vec3 uBall;
  uniform float uTime;
  varying vec2 vUv;
  varying float vPhase;

  vec3 arc(float u) {
    // The middle of the line breathes in and out a little: a live field.
    vec3 ctrl = (aStart + uBall) * 0.5 + aBow * (1.0 + 0.14 * sin(uTime * 2.6 + aPhase * 6.2832));
    float a = 1.0 - u;
    return a * a * aStart + 2.0 * a * u * ctrl + u * u * uBall;
  }

  void main() {
    float u = aParam.x;
    vec3 p = arc(u);
    vec3 along = arc(min(1.0, u + 0.02)) - arc(max(0.0, u - 0.02));
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    vec3 alongView = (modelViewMatrix * vec4(along, 0.0)).xyz;
    // Widen across the view direction, so the ribbon always faces the camera.
    vec3 side = normalize(cross(alongView, mv.xyz));
    float w = aParam.z * (0.55 + 0.45 * sin(u * 3.14159));
    mv.xyz += side * aParam.y * w;
    gl_Position = projectionMatrix * mv;
    vUv = aParam.xy;
    vPhase = aPhase;
  }
`;

const ARC_FRAG = /* glsl */ `
  uniform float uTime;
  uniform float uFade;
  uniform vec3 uColor;
  uniform vec3 uCore;
  varying vec2 vUv;
  varying float vPhase;

  void main() {
    float across = 1.0 - abs(vUv.y);
    float body = smoothstep(0.0, 0.8, across);
    float core = smoothstep(0.5, 1.0, across);
    // Pulses travel toward the ball (u rises car → ball).
    float flow = fract(vUv.x * 2.0 - uTime * 1.5 + vPhase);
    float pulse = smoothstep(0.0, 0.22, flow) * (1.0 - smoothstep(0.42, 0.95, flow));
    float ends = smoothstep(0.0, 0.07, vUv.x) * (1.0 - smoothstep(0.9, 1.0, vUv.x));
    float alpha = body * (0.32 + 0.68 * pulse) * ends * uFade;
    vec3 col = mix(uColor, uCore, core * (0.35 + 0.65 * pulse)) * (1.0 + 1.5 * pulse);
    gl_FragColor = vec4(col, alpha);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

let glowTex: THREE.CanvasTexture | null = null;
function softGlowTexture(): THREE.CanvasTexture {
  if (glowTex) return glowTex;
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const x = c.getContext('2d') as CanvasRenderingContext2D;
  const g = x.createRadialGradient(32, 32, 2, 32, 32, 31);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.35, 'rgba(255,255,255,0.55)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  x.fillStyle = g;
  x.fillRect(0, 0, 64, 64);
  glowTex = new THREE.CanvasTexture(c);
  glowTex.userData.shared = true;
  return glowTex;
}

let arcGeo: THREE.BufferGeometry | null = null;
function arcGeometry(): THREE.BufferGeometry {
  if (arcGeo) return arcGeo;
  const start: number[] = [];
  const bow: number[] = [];
  const param: number[] = [];
  const phase: number[] = [];
  const index: number[] = [];
  let v = 0;
  ARCS.forEach((a, n) => {
    for (let i = 0; i <= ARC_SEGS; i++) {
      for (const side of [-1, 1]) {
        start.push(...a.start);
        bow.push(...a.bow);
        param.push(i / ARC_SEGS, side, a.width);
        phase.push((n * 0.37) % 1);
      }
      if (i < ARC_SEGS) {
        const b = v + i * 2;
        index.push(b, b + 1, b + 2, b + 1, b + 3, b + 2);
      }
    }
    v += (ARC_SEGS + 1) * 2;
  });
  const geo = new THREE.BufferGeometry();
  // three wants a position attribute; the real positions come from the shader.
  geo.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(start.length), 3));
  geo.setAttribute('aStart', new THREE.Float32BufferAttribute(start, 3));
  geo.setAttribute('aBow', new THREE.Float32BufferAttribute(bow, 3));
  geo.setAttribute('aParam', new THREE.Float32BufferAttribute(param, 3));
  geo.setAttribute('aPhase', new THREE.Float32BufferAttribute(phase, 1));
  geo.setIndex(index);
  arcGeo = geo;
  return geo;
}

let archGeo: THREE.BufferGeometry | null = null;
function archGeometry(): THREE.BufferGeometry {
  // Half a ring (arc = PI): an arch standing on the road, facing down the car.
  archGeo ??= new THREE.TorusGeometry(1, 0.045, 6, 30, Math.PI);
  return archGeo;
}

export class MagnetField {
  readonly group = new THREE.Group();
  private readonly arcMat: THREE.ShaderMaterial;
  private readonly arcs: THREE.Mesh;
  private readonly arches: { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial }[] = [];
  private readonly rings: { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial }[] = [];
  private readonly halo: THREE.Sprite;
  private readonly haloMat: THREE.SpriteMaterial;
  private readonly lockRing: THREE.Mesh;
  private readonly lockMat: THREE.MeshBasicMaterial;
  private readonly ball = new THREE.Vector3(0, 1.6, 3.9);
  private t = 0;

  constructor(private readonly ballRadius: number) {
    this.arcMat = new THREE.ShaderMaterial({
      vertexShader: ARC_VERT,
      fragmentShader: ARC_FRAG,
      uniforms: {
        uBall: { value: this.ball },
        uTime: { value: 0 },
        uFade: { value: 0 },
        uColor: { value: VIOLET.clone().multiplyScalar(1.5) },
        uCore: { value: CORE.clone().multiplyScalar(1.6) },
      },
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.arcs = new THREE.Mesh(arcGeometry(), this.arcMat);
    this.arcs.frustumCulled = false; // positions are made in the shader
    this.arcs.renderOrder = 3;
    this.group.add(this.arcs);

    for (let i = 0; i < ARCH_COUNT; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: VIOLET.clone().multiplyScalar(1.8),
        transparent: true,
        opacity: 0,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(archGeometry(), mat);
      mesh.renderOrder = 3;
      this.arches.push({ mesh, mat });
      this.group.add(mesh);
    }

    const ringGeo = new THREE.PlaneGeometry(1, 1);
    ringGeo.rotateX(-Math.PI / 2); // flat on the road
    for (let i = 0; i < GROUND_RINGS; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: VIOLET.clone().multiplyScalar(1.6),
        map: glowRingTexture(),
        transparent: true,
        opacity: 0,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(ringGeo, mat);
      mesh.renderOrder = 2;
      this.rings.push({ mesh, mat });
      this.group.add(mesh);
    }

    this.haloMat = new THREE.SpriteMaterial({
      map: softGlowTexture(),
      color: VIOLET.clone().multiplyScalar(1.4),
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    this.halo = new THREE.Sprite(this.haloMat);
    this.group.add(this.halo);

    this.lockMat = new THREE.MeshBasicMaterial({
      color: CORE.clone().multiplyScalar(1.5),
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    this.lockRing = new THREE.Mesh(
      new THREE.TorusGeometry(ballRadius + 0.32, 0.055, 6, 36),
      this.lockMat,
    );
    this.group.add(this.lockRing);

    this.group.visible = false;
  }

  /**
   * @param fade 0..1 — how far the effect has faded in.
   * @param bx,by,bz the ball's centre in CAR-LOCAL space.
   */
  update(fade: number, bx: number, by: number, bz: number, dt: number): void {
    this.group.visible = fade > 0.02;
    if (!this.group.visible) return;
    // Reduced motion: the field stays (it is information) but barely drifts.
    this.t += dt * (itemFxCalm() ? 0.2 : 1);
    const t = this.t;
    this.ball.set(bx, by, bz);
    this.arcMat.uniforms.uTime.value = t;
    this.arcMat.uniforms.uFade.value = fade;

    // Pulse arches: tail of the car → the ball, closing in as they travel.
    const rFar = 2.6;
    const rNear = this.ballRadius + 0.4;
    for (let i = 0; i < ARCH_COUNT; i++) {
      const a = this.arches[i];
      const s = (t * 0.85 + i / ARCH_COUNT) % 1; // 0 at the car, 1 at the ball
      const r = rFar + (rNear - rFar) * Math.pow(s, 0.8);
      a.mesh.position.set(bx * s, 0.1, -1.7 + (bz + 1.7) * s);
      a.mesh.scale.setScalar(r);
      a.mat.opacity = Math.sin(Math.PI * s) * 0.85 * fade;
    }

    // Ground pulses: spread from under the car, fading as they widen.
    for (let i = 0; i < GROUND_RINGS; i++) {
      const g = this.rings[i];
      const s = (t * 0.7 + i / GROUND_RINGS) % 1;
      const size = 4 + s * 12;
      g.mesh.position.set(bx * 0.3, 0.07, bz * 0.3);
      g.mesh.scale.set(size, 1, size);
      g.mat.opacity = (1 - s) * (1 - s) * 0.8 * fade;
    }

    // The captured ball: a breathing glow and a tilted ring spinning round it.
    const breathe = 1 + Math.sin(t * 7) * 0.08;
    const hs = (this.ballRadius * 2 + 2.4) * breathe;
    this.halo.position.set(bx, by, bz);
    this.halo.scale.set(hs, hs, 1);
    this.haloMat.opacity = 0.6 * fade;
    this.lockRing.position.set(bx, by, bz);
    this.lockRing.rotation.set(1.15 + Math.sin(t * 1.3) * 0.25, t * 2.4, 0);
    this.lockMat.opacity = 0.9 * fade;
  }

  dispose(): void {
    this.arcMat.dispose();
    for (const a of this.arches) a.mat.dispose();
    for (const g of this.rings) g.mat.dispose();
    this.haloMat.dispose();
    this.lockMat.dispose();
    this.lockRing.geometry.dispose();
  }
}
